/**
 * Pure(ish) canvas helpers for the Smart Create AI flow. Extracted from
 * EditorPage so they can be unit-tested in isolation and reused by any
 * future AI tool that needs the same inpaint plumbing.
 *
 * All helpers are synchronous canvas-in / canvas-out except
 * `loadImageToCanvas` which awaits the underlying `<img>` load.
 */

/** Smart Create Insert max edge length sent to the model. OpenAI's
 *  edit endpoint outputs at most 1536² and BFL FLUX's largest accepted
 *  size is also 1536 on the long axis, so anything larger is wasted
 *  bandwidth before the model even runs. The wrapper's compositor
 *  resizes the result back to the layer's native resolution before
 *  destination-in clipping, so downscaling on send costs no fidelity
 *  inside the marquee region. */
export const SMART_CREATE_MAX_INPUT_EDGE_PX = 1536;

/** Inpaint mask-edge feather defaults. The mask boundary fades over a
 *  band proportional to the marquee's smaller side, clamped so very
 *  small marquees still get a perceptible transition and very large
 *  marquees don't dissolve too far inward. The default 5% matches a
 *  typical "object-sized" marquee comfortably; the slider in the
 *  Inpaint sub-panel exposes 0–20% so users can tune softer/harder
 *  edges without touching code. */
export const FEATHER_RADIUS_RATIO_DEFAULT = 0.05;
export const FEATHER_RADIUS_RATIO_MIN = 0;
export const FEATHER_RADIUS_RATIO_MAX = 0.2;
export const FEATHER_RADIUS_MIN_PX = 8;
export const FEATHER_RADIUS_MAX_PX = 32;

/** Compute the feather radius (in pixels) for a Blend-mode mask given
 *  the marquee's bbox and a ratio (0–0.2). Pure math — `featherMask`
 *  consumes the result. The ratio defaults to
 *  `FEATHER_RADIUS_RATIO_DEFAULT` so existing call sites that don't
 *  pass a ratio retain their previous behavior. */
export function computeFeatherRadius(
  bboxWidth: number,
  bboxHeight: number,
  ratio: number = FEATHER_RADIUS_RATIO_DEFAULT,
): number {
  // A ratio of 0 disables feathering entirely (hard edges); we still
  // clamp the px result at MIN_PX so any positive ratio gets a
  // perceptible transition.
  if (ratio <= 0) return 0;
  return Math.max(
    FEATHER_RADIUS_MIN_PX,
    Math.min(FEATHER_RADIUS_MAX_PX, Math.round(Math.min(bboxWidth, bboxHeight) * ratio)),
  );
}

/** Compute the destination dimensions for `fitCanvasToMaxDim` without
 *  actually performing the resize. Pure math — separable so callers
 *  that only need the dims (e.g., to budget memory) can skip the draw.
 *
 *  When `minDim > 0`, also enforces a per-side floor: if the shorter
 *  side is below `minDim`, the whole canvas scales UP to bring it up.
 *  Stability's `/edit/erase` endpoint (and several other v2beta
 *  endpoints) require every side ≥ 64 px and 400 with a "unsupported
 *  dimensions" error otherwise — small marquees would hit that without
 *  this upscale.
 *
 *  Min-floor wins over max-cap when both apply (a 30×4000 input would
 *  scale up to 64×8533 first, then... actually the contradiction
 *  shouldn't arise in practice because the editor's marquees never
 *  produce extreme aspect ratios).
 */
export function computeFitDims(
  width: number,
  height: number,
  maxDim: number,
  minDim: number = 0,
): { width: number; height: number; scale: number } {
  const longest = Math.max(width, height);
  const shortest = Math.min(width, height);

  let scale = 1;
  if (minDim > 0 && shortest < minDim) {
    // Smaller side is below the floor — scale up to hit minDim.
    scale = minDim / shortest;
  } else if (longest > maxDim) {
    scale = maxDim / longest;
  }

  if (scale === 1) return { width, height, scale };
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  };
}

/** Build a fully-opaque grayscale mask for engines that read the mask
 *  as grayscale where white = replace (Stability, Recraft). The editor's
 *  marquee mask is white-with-alpha=255 inside and transparent-black
 *  outside; this paints the marquee on a black background so:
 *    • Inside marquee: RGB(255,255,255), alpha=255 → "erase" ✓
 *    • Outside marquee: RGB(0,0,0), alpha=255 → "preserve" ✓
 *  Removes any ambiguity from the provider's PNG decoder treating
 *  alpha=0 pixels as undefined. NOT for OpenAI — that endpoint uses
 *  alpha-based masking (see `invertMaskAlpha`). */
export function toGrayscaleMask(mask: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = mask.width;
  out.height = mask.height;
  const ctx = out.getContext('2d');
  if (!ctx) return mask;
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, out.width, out.height);
  // The editor mask is white inside marquee with alpha=255, transparent
  // outside. Drawing it over the black background with default
  // source-over: white-alpha-255 pixels overwrite black; transparent
  // pixels leave black untouched.
  ctx.drawImage(mask, 0, 0);
  return out;
}

/** Convert the editor's marquee mask (alpha=255 inside the selection,
 *  alpha=0 outside) to OpenAI's edit-mask convention (alpha=0 inside,
 *  alpha=255 outside). Returns a fresh canvas the same size as the
 *  source — the caller's canvas is left untouched. */
export function invertMaskAlpha(mask: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = mask.width;
  out.height = mask.height;
  const ctx = out.getContext('2d');
  if (!ctx) return mask;
  // Start fully opaque white, then punch a transparent hole using the
  // mask's filled region (destination-out reads the mask's alpha and
  // subtracts it from the destination).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.drawImage(mask, 0, 0);
  return out;
}

/** Resize a canvas to fit within `[minDim, maxDim]` per side, preserving
 *  aspect ratio. Returns the input unchanged when no scaling is needed.
 *
 *  - Down-scales when the longest side > `maxDim` (Smart Create's
 *    payload cap + OpenAI's 1536² output ceiling).
 *  - Up-scales when the shortest side < `minDim` (Stability's per-side
 *    floor of 64 px on `/edit/erase` etc.) — only when `minDim > 0`.
 *
 *  The up-scaled output is then sent to the model; the result comes
 *  back at the same up-scaled dimensions and the caller draws it back
 *  into the original (un-scaled) crop rect via canvas auto-scaling, so
 *  there's no visible quality regression for the user. */
/** Per-engine input-side floors. Each provider rejects images below a
 *  certain dimension; up-scaling small marquees client-side avoids
 *  silent 4xx errors from the provider's API. */
export const STABILITY_MIN_INPUT_EDGE_PX = 64;
export const RECRAFT_MIN_INPUT_EDGE_PX = 256;
export const OPENAI_MIN_INPUT_EDGE_PX = 256;

export function fitCanvasToMaxDim(
  src: HTMLCanvasElement,
  maxDim: number,
  minDim: number = 0,
): HTMLCanvasElement {
  const dims = computeFitDims(src.width, src.height, maxDim, minDim);
  if (dims.scale === 1) return src;
  const out = document.createElement('canvas');
  out.width = dims.width;
  out.height = dims.height;
  const ctx = out.getContext('2d');
  if (!ctx) return src;
  ctx.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

/** Soften a hard binary mask into a feathered alpha gradient that
 *  fades to zero at the marquee boundary. Used by Smart Create's
 *  inpaint mode so the AI-generated content blends into the
 *  surrounding photo instead of meeting it at a hard edge.
 *
 *  Approach: blur the mask, then remap the alpha through
 *  `clamp((a - 128) * 2)` so the blurred edge's 50% point lands at
 *  alpha=0. The result has alpha=255 in the marquee's interior
 *  (eroded inward by ~radius), tapering to 0 right at the marquee's
 *  original boundary — no halo extending outward, no hard cut at the
 *  edge. */
export function featherMask(mask: HTMLCanvasElement, radius: number): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = mask.width;
  out.height = mask.height;
  const ctx = out.getContext('2d');
  if (!ctx) return mask;
  ctx.filter = `blur(${radius}px)`;
  ctx.drawImage(mask, 0, 0);
  ctx.filter = 'none';
  const img = ctx.getImageData(0, 0, out.width, out.height);
  for (let i = 3; i < img.data.length; i += 4) {
    const v = img.data[i];
    img.data[i] = Math.max(0, Math.min(255, (v - 128) * 2));
  }
  ctx.putImageData(img, 0, 0);
  return out;
}

/**
 * Trim transparent edges and flatten remaining alpha onto opaque white.
 * Used by AI tools that send the active layer's pixels to Stability /
 * Recraft (Upscale, Vectorize, Control). Neither provider preserves
 * alpha — they composite transparent regions onto their own default
 * color (Stability=black, Recraft=white) before processing. Without
 * this pre-pass the result comes back framed by a provider-specific
 * border. Trimming + flattening guarantees the export only ever sees
 * an opaque rectangle of the actual content.
 *
 *   1. Scan the alpha channel to find the bounding box of non-transparent
 *      pixels. Single O(W·H) pass — fine at 1024² source sizes (4 ms
 *      typical), would need a sampled fast-path for >4 MP inputs.
 *   2. If everything's already opaque AND the bbox is the whole canvas,
 *      return the source unchanged (cheapest path, no extra allocation).
 *   3. Otherwise build a fresh canvas at bbox dims, fill white, composite
 *      the trimmed source on top. Internal alpha holes (e.g. a donut-
 *      shape PNG) are flattened onto the white fill — that's a deliberate
 *      compromise; the providers can't preserve alpha anyway, so a
 *      consistent white background beats a surprise per-provider border.
 *
 * Returns the source canvas itself when nothing needed trimming, so
 * callers can rely on identity-equality to detect "no flatten was
 * needed" without extra allocation.
 */
export function flattenForExport(src: HTMLCanvasElement): HTMLCanvasElement {
  const w = src.width;
  const h = src.height;
  if (w <= 0 || h <= 0) return src;
  const ctx = src.getContext('2d');
  if (!ctx) return src;
  let pixelData: Uint8ClampedArray;
  try {
    pixelData = ctx.getImageData(0, 0, w, h).data;
  } catch {
    // Tainted canvas (cross-origin source). Bail — the provider call
    // will succeed or fail on its own; we can't read pixels here.
    return src;
  }

  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  let hasAlphaHoles = false;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = pixelData[(y * w + x) * 4 + 3];
      if (a === 255) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      } else if (a > 0) {
        // Partial alpha — the bbox should still include this pixel
        // so a soft edge isn't trimmed off.
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
        hasAlphaHoles = true;
      } else {
        hasAlphaHoles = true;
      }
    }
  }

  if (maxX < 0) return src; // entirely transparent — nothing to upscale

  const trimmedW = maxX - minX + 1;
  const trimmedH = maxY - minY + 1;
  const fullyTight = trimmedW === w && trimmedH === h;
  if (fullyTight && !hasAlphaHoles) return src; // already opaque + tight

  const out = document.createElement('canvas');
  out.width = trimmedW;
  out.height = trimmedH;
  const outCtx = out.getContext('2d');
  if (!outCtx) return src;
  // Opaque white fill first so any partial-alpha or interior-hole
  // pixels composite onto a consistent neutral color rather than the
  // canvas's default transparent black (which would re-create the
  // exact bug we're fixing).
  outCtx.fillStyle = '#ffffff';
  outCtx.fillRect(0, 0, trimmedW, trimmedH);
  outCtx.drawImage(src, minX, minY, trimmedW, trimmedH, 0, 0, trimmedW, trimmedH);
  return out;
}

/** Decode a data URL into an HTMLCanvasElement. Used to convert the
 *  Smart Create response back into something the canvas hook can
 *  composite. */
export function loadImageToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const out = document.createElement('canvas');
      out.width = img.naturalWidth;
      out.height = img.naturalHeight;
      const ctx = out.getContext('2d');
      if (!ctx) {
        reject(new Error('2D context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(out);
    };
    img.onerror = () => reject(new Error('Failed to decode generated image'));
    img.src = dataUrl;
  });
}
